import React from 'react';
import { TableRow, TableCell, IconButton, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { prettyDate, toCapitalizeFromUnderScore } from '../../../lib/helpers/helpers';

const useStyles = makeStyles((theme) => ({
  // Row styles
  expandableRow: {
    cursor: 'pointer',
    height: 52,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '& .MuiTableCell-root': {
      height: 52,
    },
  },
  expandedRow: {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  expandedRowCell: {
    height: 52,
  },

  // Cell styles
  iconCell: {
    width: '56px',
    padding: '8px 0 8px 16px',
    verticalAlign: 'middle',
  },
  questionCell: {
    width: '170px',
    padding: '0 10px',
  },
  answerCell: {
    width: '333px',
    padding: '0 10px',
  },
  dateCell: {
    width: '117px',
    padding: '0 10px',
  },
  assessmentCell: {
    width: '136px',
    maxWidth: '136px',
    minWidth: '136px',
    padding: '0 10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // Text and icon styles
  questionText: {
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  answerText: {
    maxWidth: '400px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  iconButtonAlign: {
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  expandIcon: {
    transition: 'transform 0.2s',
    fontSize: '20px',
  },
  expandIconExpanded: {
    transform: 'rotate(90deg)',
  },
}));

// Row component to handle question rows
const PatientCustomQuestionsRow = ({ question, toggleRowExpanded, expandedRows, getAssessmentLink, theme }) => {
  const classes = useStyles();
  const questionId = question?.id || question?.short_name;
  const isExpanded = expandedRows[questionId] || false;

  return (
    <React.Fragment>
      <TableRow
        className={classes.expandableRow}
        onClick={() => toggleRowExpanded(questionId)}
        hover
      >
        <TableCell className={classes.iconCell}>
          <IconButton
            className={classes.iconButtonAlign}
            onClick={(e) => {
              e.stopPropagation();
              toggleRowExpanded(questionId);
            }}
            size="small"
          >
            <ChevronRightIcon className={`${classes.expandIcon} ${isExpanded ? classes.expandIconExpanded : ''}`} />
          </IconButton>
        </TableCell>
        <TableCell className={classes.questionCell}>
          <Tooltip
            title={toCapitalizeFromUnderScore(question?.short_name) || 'N/A'}
            placement="top"
            arrow
          >
            <div className={classes.questionText}>{toCapitalizeFromUnderScore(question?.short_name) ?? 'N/A'}</div>
          </Tooltip>
        </TableCell>
        <TableCell className={classes.answerCell}>
          <div className={classes.answerText}>
            {typeof question?.most_recent_answer?.formatted_answer_text === 'object'
              ? question?.most_recent_answer?.formatted_answer_text?.address ?? 'N/A'
              : question?.most_recent_answer?.formatted_answer_text ?? 'N/A'}
          </div>
        </TableCell>
        <TableCell className={classes.dateCell}>
          {question?.most_recent_answer?.created_at ? prettyDate(question.most_recent_answer.created_at) : 'N/A'}
        </TableCell>
        <TableCell className={classes.assessmentCell}>
          {getAssessmentLink(question?.most_recent_answer?.workflow_id)}
        </TableCell>
      </TableRow>

      {isExpanded &&
        question?.custom_question_answers?.map((answer, index) => (
          <TableRow
            key={answer?.id ?? `${questionId}-answer-${index}`}
            className={classes.expandedRow}
            style={{ height: 52 }}
          >
            <TableCell className={`${classes.iconCell} ${classes.expandedRowCell}`}></TableCell>
            <TableCell className={`${classes.questionCell} ${classes.expandedRowCell}`}></TableCell>
            <TableCell className={`${classes.answerCell} ${classes.expandedRowCell}`}>
              {typeof answer?.formatted_answer_text === 'object'
                ? answer?.formatted_answer_text?.address ?? 'N/A'
                : answer?.formatted_answer_text ?? 'N/A'}
            </TableCell>
            <TableCell className={`${classes.dateCell} ${classes.expandedRowCell}`}>
              {answer?.created_at ? prettyDate(answer.created_at) : 'N/A'}
            </TableCell>
            <TableCell className={`${classes.assessmentCell} ${classes.expandedRowCell}`}>
              {getAssessmentLink(answer?.workflow_id)}
            </TableCell>
          </TableRow>
        ))}
    </React.Fragment>
  );
};

export default PatientCustomQuestionsRow;
